import { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, Users, Calendar } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { toast } from 'react-hot-toast';
import { sendStaffAppointmentEmail } from '@/lib/staffAppointmentConfirmation';
import { writeTreatmentCoursePatientParams } from '@/lib/treatmentCoursePatientParams';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  combineClinicAndPractitionerPeriods,
  exceptionByDate,
  getEffectiveBookableWindows,
  isClosedHoursException,
  outsideShownCalendarHours,
  parseCivilDate,
  parseWeeklyHours,
  resolveClinicHoursForLocalDate,
  resolvedToCalendarDayHours,
  weeklyHoursByJsDay,
} from '@/lib/clinicHours';
import { practitionerDateHoursKey, unionPeriods } from '@/lib/practitionerAvailability';
import {
  buildPractitionerColumnHours,
  memberExceptionsFromClinicRows,
} from '@/lib/practitionerHours';
import { useClinicHoursExceptions } from '@/hooks/useClinicHoursExceptions';
import {
  toMemberDayExceptions,
  useMemberScheduleExceptions,
} from '@/hooks/usePractitionerDayHours';
import { WORKING_HOURS_DAY_KEYS } from '@/utils/appointmentCalendarDates';
import {
  useAppointmentsInRange,
  useRescheduleAppointment,
  useUpdateAppointmentDuration,
  useAppointmentsRealtime,
  appointmentsRangeQueryKey,
  type AppointmentListItem,
  type ListFilters,
} from '@/hooks/useAppointments';
import { useGroupSessionsInRange, useGroupSessionsRealtime } from '@/hooks/useGroupSessions';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import { useClinic } from '@/hooks/useClinicSettings';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/ui/PageLayout';
import { ToolbarCluster, ToolbarSegment } from '@/components/ui/PageHeader';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Banner } from '@/components/ui/Banner';
import { EmptyState } from '@/components/ui/EmptyState';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Card } from '@/components/ui/Card';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { CalendarLegend } from './CalendarLegend';
import { WeekStrip } from './WeekStrip';
import {
  appointmentPeriodLabelWidthSamples,
  formatAppointmentClock,
  formatAppointmentRangeLabel,
} from '@/utils/appointmentTimeFormat';
import { ClinicDayHoursModal } from './ClinicDayHoursModal';
import { PractitionerDayHoursModal } from './PractitionerDayHoursModal';
import { AppointmentModal } from './AppointmentModal';
import { AppointmentDetailsModal } from './AppointmentDetailsModal';
import { GroupSessionModal } from './GroupSessionModal';
import { BookTreatmentCourseSlideOver } from '@/components/appointments/BookTreatmentCourseSlideOver';
import { MonthView } from './MonthView';
import { AgendaView } from './AgendaView';
import { RescheduleConfirmationModal } from '@/components/appointments/RescheduleConfirmationModal';
import { SlotActionMenu } from '@/components/appointments/SlotActionMenu';
import { useCalendarAppointmentMeta } from '@/hooks/useCalendarAppointmentMeta';
import { AppointmentGhost, CalendarGrid, type DragData, type DropData } from './CalendarGrid';
import { CalendarResourceFilter } from './CalendarResourceFilter';
import {
  useOccupyingAllocationsForAppointments,
  useResourceGroups,
  useResources,
} from '@/hooks/useSchedulingResources';
import {
  CALENDAR_PERSPECTIVE_STORAGE_KEY,
  buildResourceCalendarColumns,
  occupyingResourceIdsByAppointment,
  parseCalendarPerspective,
  type CalendarPerspective,
} from '@/utils/appointmentResourceCalendar';
import {
  CALENDAR_COLUMN_HEADER_PX,
  CALENDAR_STAFF_HEADER_PX,
  calendarHoursMidpoint,
  calendarOpeningHoursRange,
  fitHourHeight,
  intersectCalendarDayHours,
  visibleCalendarHours,
} from '@/utils/appointmentCalendarStyle';
import {
  APPOINTMENTS_STAFF_ALL_EMPLOYEES,
  isAppointmentsAllEmployees,
  readRememberedAppointmentsStaff,
  resolveAppointmentsStaffFilter,
  staffSearchValueFromSelection,
  usableRememberedAppointmentsStaff,
  writeRememberedAppointmentsStaff,
} from '@/utils/resolveAppointmentsStaffFilter';
import {
  appointmentRescheduleErrorI18nKey,
  classifyAppointmentRescheduleError,
} from '@/utils/appointmentRescheduleError';
import { appointmentSaveErrorI18nKey } from '@/utils/appointmentSaveError';
import {
  countedPatientAppointments,
  dragBlockOverlapLabel,
  isCalendarBlock,
} from '@/utils/calendarTimeBlock';
import {
  appointmentFiltersToSearchParams,
  parseAppointmentCalendarView,
  parseAppointmentFiltersFromSearch,
} from './appointmentFilters';
import {
  CLINIC_TIMEZONE,
  anchorForCalendarView,
  clinicAddDays,
  clinicAddMonths,
  clinicDayKey,
  clinicEndOfDay,
  clinicMinutesFromMidnight,
  clinicMonthBounds,
  clinicStartOfDay,
  clinicWeekDays,
  clinicWeekdayIndex,
  parseClinicDayKey,
  sameClinicDay,
} from '@/utils/calendarClinicTime';
import { zonedInstant } from '@/lib/reporting/timezone';

type View = 'day' | 'week' | 'month' | 'agenda';
type LayoutView = 'time' | 'resource';

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function snapToFifteenMin(deltaY: number, hourHeight: number): number {
  const pxPer15Min = hourHeight / 4;
  return Math.round(deltaY / pxPer15Min) * 15;
}

// ---------------------------------------------------------------------------
// Appointments page
// ---------------------------------------------------------------------------
export function Appointments() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isClinicAdmin, isPlatformAdmin, hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const qc = useQueryClient();

  const { data: clinic, isPending: clinicPending } = useClinic(activeClinicId);
  const { data: members, isPending: membersPending } = useClinicMembers(activeClinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(activeClinicId);

  const [view, setView] = useState<View>(() => {
    const fromUrl = parseAppointmentCalendarView(searchParams.get('view'));
    if (fromUrl) return fromUrl;
    const stored = localStorage.getItem('appointments-view');
    return stored && ['day', 'week', 'month', 'agenda'].includes(stored)
      ? (stored as View)
      : 'week';
  });
  const [layoutView, setLayoutView] = useState<LayoutView>('time');
  const [perspective, setPerspective] = useState<CalendarPerspective>(() =>
    parseCalendarPerspective(localStorage.getItem(CALENDAR_PERSPECTIVE_STORAGE_KEY)),
  );
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[] | null>(null);
  const resourceViewActive = perspective === 'resources';
  const resourceColumnsActive =
    resourceViewActive && (view === 'day' || view === 'week' || layoutView === 'resource');
  const [anchor, setAnchor] = useState<Date>(
    () => parseClinicDayKey(searchParams.get('date')) ?? clinicStartOfDay(new Date()),
  );
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[] | null>(null);

  const practitionerMembers = useMemo(
    () => (rolesReady ? filterPractitionerMembers(members ?? [], roles ?? []) : (members ?? [])),
    [members, roles, rolesReady],
  );
  const rememberedStaff = useMemo(() => {
    if (searchParams.has('staff')) return null;
    return usableRememberedAppointmentsStaff({
      remembered: readRememberedAppointmentsStaff(user?.id, activeClinicId),
      memberIds: membersPending ? null : (members ?? []).map((member) => member.user_id),
      practitionerIds: rolesReady ? practitionerMembers.map((member) => member.user_id) : null,
    });
  }, [
    searchParams,
    user?.id,
    activeClinicId,
    members,
    membersPending,
    rolesReady,
    practitionerMembers,
  ]);
  const staffParam = searchParams.has('staff') ? searchParams.get('staff') : rememberedStaff;

  const clinicianFilter = useMemo(
    () =>
      resolveAppointmentsStaffFilter({
        staffParam,
        hasExplicitStaffParam: searchParams.has('staff') || rememberedStaff != null,
        currentUserId: user?.id,
        memberIds: membersPending ? null : (members ?? []).map((m) => m.user_id),
      }),
    [searchParams, staffParam, rememberedStaff, user?.id, members, membersPending],
  );

  const showAllEmployees = isAppointmentsAllEmployees(staffParam);
  const limitCalendarToPractitioners =
    !resourceViewActive &&
    layoutView === 'time' &&
    !clinicianFilter &&
    !showAllEmployees &&
    rolesReady;

  function setClinicianFilter(value: string | null) {
    const staff =
      value === APPOINTMENTS_STAFF_ALL_EMPLOYEES
        ? APPOINTMENTS_STAFF_ALL_EMPLOYEES
        : staffSearchValueFromSelection(value);
    writeRememberedAppointmentsStaff(user?.id, activeClinicId, staff);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('staff', staff);
        return next;
      },
      { replace: true },
    );
  }

  // Active drag (reschedule or resize) — used to render DragOverlay
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  // Id of the appointment that caused a conflict — shows red ring briefly
  const [conflictAptId, setConflictAptId] = useState<string | null>(null);
  // Pending reschedule awaiting confirmation
  const [pendingReschedule, setPendingReschedule] = useState<{
    appointment: AppointmentListItem;
    newStartsAt: Date;
    newEndsAt: Date;
    newClinicianId: string | null;
    oldStartsAt: Date;
    oldEndsAt: Date;
    outsideOpeningHours: boolean;
    closedException: boolean;
    blockOverlapLabel: string;
  } | null>(null);
  const [slotMenu, setSlotMenu] = useState<{
    start: Date;
    end: Date | null;
    clinicianId: string | null;
    resourceIds?: string[];
    x: number;
    y: number;
  } | null>(null);
  const [hoursDate, setHoursDate] = useState<Date | null>(null);
  const [practitionerHoursTarget, setPractitionerHoursTarget] = useState<{
    date: Date;
    userId: string;
  } | null>(null);

  // Persist view selection to localStorage
  useEffect(() => {
    localStorage.setItem('appointments-view', view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem(CALENDAR_PERSPECTIVE_STORAGE_KEY, perspective);
  }, [perspective]);

  const urlAppointmentFilters = parseAppointmentFiltersFromSearch(searchParams);

  useEffect(() => {
    if (urlAppointmentFilters.view) {
      setView((current) =>
        current === urlAppointmentFilters.view ? current : urlAppointmentFilters.view!,
      );
    }
    if (urlAppointmentFilters.date) {
      const parsed = parseClinicDayKey(urlAppointmentFilters.date);
      if (parsed) {
        setAnchor((current) => (sameClinicDay(current, parsed) ? current : parsed));
      }
    }
  }, [urlAppointmentFilters.view, urlAppointmentFilters.date]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has('view') && !prev.has('date')) return prev;
        const next = appointmentFiltersToSearchParams({ view, date: clinicDayKey(anchor) }, prev);
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true },
    );
  }, [view, anchor, setSearchParams]);

  const days = useMemo(() => {
    // Staff-column layout is a single day. Resource perspective uses Day/Week instead.
    if (!resourceViewActive && layoutView === 'resource') return [clinicStartOfDay(anchor)];
    if (view === 'day') return [clinicStartOfDay(anchor)];
    if (view === 'month' || view === 'agenda') {
      const bounds = clinicMonthBounds(anchor);
      return [bounds.start, bounds.end];
    }
    return clinicWeekDays(anchor);
  }, [view, anchor, layoutView, resourceViewActive]);

  const range = useMemo<ListFilters>(
    () => ({
      start: days[0]?.toISOString() ?? new Date().toISOString(),
      end: clinicEndOfDay(days[days.length - 1] ?? new Date()).toISOString(),
      clinicianId: !resourceViewActive && layoutView === 'resource' ? null : clinicianFilter,
    }),
    [days, clinicianFilter, layoutView, resourceViewActive],
  );

  const {
    data: appointments,
    isLoading,
    error,
    refetch,
  } = useAppointmentsInRange(activeClinicId, range);
  const { data: groupSessions } = useGroupSessionsInRange(activeClinicId, range);
  const practitionerIdSet = useMemo(
    () => new Set(practitionerMembers.map((member) => member.user_id)),
    [practitionerMembers],
  );
  const calendarAppointments = useMemo(() => {
    const rows = appointments ?? [];
    if (!limitCalendarToPractitioners) return rows;
    return rows.filter(
      (appointment) =>
        appointment.clinician_id != null && practitionerIdSet.has(appointment.clinician_id),
    );
  }, [appointments, limitCalendarToPractitioners, practitionerIdSet]);
  const calendarGroupSessions = useMemo(() => {
    const rows = groupSessions ?? [];
    if (!limitCalendarToPractitioners) return rows;
    return rows.filter(
      (session) => session.clinician_id != null && practitionerIdSet.has(session.clinician_id),
    );
  }, [groupSessions, limitCalendarToPractitioners, practitionerIdSet]);
  const { data: resourceGroups } = useResourceGroups(activeClinicId);
  const { data: clinicResources } = useResources(activeClinicId);

  const staffColumns = useMemo(() => {
    if (resourceViewActive) return [];
    if (!members?.length) return [];
    if (layoutView === 'resource') {
      const staffList =
        selectedStaffIds === null
          ? members
          : members.filter((m) => selectedStaffIds.includes(m.user_id));
      return staffList.map((m) => ({ id: m.user_id, name: m.name }));
    }
    if (clinicianFilter) return [];
    const columnMembers =
      layoutView === 'time' && !showAllEmployees && rolesReady ? practitionerMembers : members;
    return columnMembers.map((m) => ({ id: m.user_id, name: m.name }));
  }, [
    layoutView,
    members,
    selectedStaffIds,
    clinicianFilter,
    resourceViewActive,
    showAllEmployees,
    rolesReady,
    practitionerMembers,
  ]);

  const resourceColumns = useMemo(
    () =>
      buildResourceCalendarColumns(
        resourceGroups ?? [],
        clinicResources ?? [],
        selectedResourceIds,
      ),
    [resourceGroups, clinicResources, selectedResourceIds],
  );
  const gridColumns = resourceColumnsActive ? resourceColumns : staffColumns;
  const appointmentIds = useMemo(
    () => (appointments ?? []).map((appointment) => appointment.id),
    [appointments],
  );
  const { data: occupyingAllocations } = useOccupyingAllocationsForAppointments(
    activeClinicId,
    appointmentIds,
    resourceColumnsActive,
  );
  const resourceIdsByAppointment = useMemo(
    () => occupyingResourceIdsByAppointment(occupyingAllocations ?? []),
    [occupyingAllocations],
  );
  const hasActiveResources = (clinicResources ?? []).some((resource) => resource.is_active);

  const calendarMeta = useCalendarAppointmentMeta(activeClinicId, appointments ?? []);

  const openingHoursMembers = useMemo(() => {
    if (!members?.length) return [];
    if (layoutView === 'resource') {
      if (selectedStaffIds === null) return members;
      return members.filter((member) => selectedStaffIds.includes(member.user_id));
    }
    if (clinicianFilter) {
      return members.filter((member) => member.user_id === clinicianFilter);
    }
    if (!resourceViewActive && layoutView === 'time' && !showAllEmployees && rolesReady) {
      return practitionerMembers;
    }
    return members;
  }, [
    members,
    layoutView,
    selectedStaffIds,
    clinicianFilter,
    showAllEmployees,
    rolesReady,
    practitionerMembers,
    resourceViewActive,
  ]);

  const clinicHoursByDay = useMemo(
    () => weeklyHoursByJsDay(parseWeeklyHours(clinic?.business_hours)),
    [clinic?.business_hours],
  );

  const weeklyHours = useMemo(
    () => parseWeeklyHours(clinic?.business_hours),
    [clinic?.business_hours],
  );

  const exceptionRange = useMemo(() => {
    if (view === 'month') {
      const gridStart = clinicWeekDays(clinicMonthBounds(anchor).start)[0] ?? anchor;
      const gridEnd = clinicAddDays(gridStart, 41);
      return { from: clinicDayKey(gridStart), to: clinicDayKey(gridEnd) };
    }
    const start = days[0];
    const end = days[days.length - 1];
    if (!start || !end) return { from: null as string | null, to: null as string | null };
    return { from: clinicDayKey(start), to: clinicDayKey(end) };
  }, [days, view, anchor]);

  const { data: hourExceptions } = useClinicHoursExceptions(
    activeClinicId,
    exceptionRange.from,
    exceptionRange.to,
  );
  const { data: memberExceptions } = useMemberScheduleExceptions(
    activeClinicId,
    exceptionRange.from,
    exceptionRange.to,
  );
  const exceptionsMap = useMemo(() => exceptionByDate(hourExceptions), [hourExceptions]);
  const practitionerExceptions = useMemo(
    () => memberExceptionsFromClinicRows(memberExceptions),
    [memberExceptions],
  );

  const { resolvedHoursByDate, resolvedHoursByPractitionerDate, clinicHoursByDate } =
    useMemo(() => {
      const byDate = new Map<string, ReturnType<typeof resolvedToCalendarDayHours>>();
      const byPractitioner = new Map<string, ReturnType<typeof resolvedToCalendarDayHours>>();
      const byClinic = new Map<string, ReturnType<typeof resolvedToCalendarDayHours>>();
      const start = exceptionRange.from ? parseCivilDate(exceptionRange.from) : days[0];
      const end = exceptionRange.to ? parseCivilDate(exceptionRange.to) : days[days.length - 1];
      if (!start || !end) {
        return {
          resolvedHoursByDate: byDate,
          resolvedHoursByPractitionerDate: byPractitioner,
          clinicHoursByDate: byClinic,
        };
      }
      const cursor = new Date(start);
      cursor.setHours(0, 0, 0, 0);
      const last = new Date(end);
      last.setHours(0, 0, 0, 0);
      while (cursor.getTime() <= last.getTime()) {
        const iso = clinicDayKey(cursor);
        const resolved = resolveClinicHoursForLocalDate({
          date: cursor,
          weeklyHours,
          exception: exceptionsMap.get(iso) ?? null,
        });
        byClinic.set(iso, resolvedToCalendarDayHours(resolved));
        const dayKey = WORKING_HOURS_DAY_KEYS[cursor.getDay()];
        const memberWindows = openingHoursMembers.map((member) => {
          const exceptions = toMemberDayExceptions(memberExceptions, member.membership_id, iso);
          const practitionerPeriods = getEffectiveBookableWindows({
            clinicPeriods: resolved.periods,
            clinicSource: resolved.source,
            clinicExceptionType: resolved.exceptionType,
            clinicianDay: member.working_hours[dayKey],
            memberExceptions: exceptions,
          });
          const periods = combineClinicAndPractitionerPeriods(resolved, practitionerPeriods);
          const memberOverride = exceptions.length > 0;
          const dayHours = resolvedToCalendarDayHours({
            ...resolved,
            enabled: periods.length > 0,
            periods,
            source: memberOverride ? 'exception' : resolved.source,
            exceptionType: memberOverride
              ? periods.length > 0
                ? 'custom_hours'
                : 'closed'
              : resolved.exceptionType,
          });
          byPractitioner.set(practitionerDateHoursKey(member.user_id, iso), dayHours);
          return periods;
        });
        const periods =
          openingHoursMembers.length > 0 ? unionPeriods(memberWindows) : resolved.periods;
        const single =
          openingHoursMembers.length === 1
            ? byPractitioner.get(practitionerDateHoursKey(openingHoursMembers[0].user_id, iso))
            : null;
        byDate.set(
          iso,
          single ??
            resolvedToCalendarDayHours({
              ...resolved,
              enabled: periods.length > 0,
              periods,
            }),
        );
        cursor.setDate(cursor.getDate() + 1);
      }
      return {
        resolvedHoursByDate: byDate,
        resolvedHoursByPractitionerDate: byPractitioner,
        clinicHoursByDate: byClinic,
      };
    }, [days, weeklyHours, exceptionsMap, openingHoursMembers, memberExceptions, exceptionRange]);

  const practitionerColumnHours = useMemo(() => {
    if (resourceColumnsActive) return new Map();
    const columnUserIds = staffColumns.length
      ? staffColumns.map((column) => column.id)
      : clinicianFilter
        ? [clinicianFilter]
        : (members ?? []).map((member) => member.user_id);
    const raw = buildPractitionerColumnHours({
      days,
      members: members ?? [],
      exceptions: practitionerExceptions,
      columnUserIds,
    });
    const combined = new Map<
      string,
      Map<string, NonNullable<ReturnType<typeof intersectCalendarDayHours>>>
    >();
    for (const [iso, byColumn] of raw) {
      const clinic = clinicHoursByDate.get(iso);
      const next = new Map<string, NonNullable<ReturnType<typeof intersectCalendarDayHours>>>();
      for (const [userId, hours] of byColumn) {
        next.set(userId, intersectCalendarDayHours(clinic, hours) ?? hours);
      }
      combined.set(iso, next);
    }
    return combined;
  }, [
    resourceColumnsActive,
    staffColumns,
    clinicianFilter,
    members,
    practitionerExceptions,
    days,
    clinicHoursByDate,
  ]);

  const openingHoursMidpoint = useMemo(() => {
    const fromResolved = [...resolvedHoursByDate.values()];
    const resolvedRange = calendarOpeningHoursRange(fromResolved);
    if (resolvedRange) return calendarHoursMidpoint(resolvedRange);

    const fromClinic = days.map(
      (day) =>
        resolvedHoursByDate.get(clinicDayKey(day)) ?? clinicHoursByDay.get(day.getDay()) ?? null,
    );
    return calendarHoursMidpoint(calendarOpeningHoursRange(fromClinic));
  }, [days, clinicHoursByDay, resolvedHoursByDate]);

  const openingHoursReady = !membersPending && !clinicPending;
  const canEditHours = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)),
  );

  const bookingHours = useMemo(() => weeklyHoursByJsDay(weeklyHours), [weeklyHours]);

  // Full day. Closed time stays grey via the hour overlays; scroll centres the open window.
  const visibleHours = visibleCalendarHours();

  const calendarFitRef = useRef<HTMLDivElement>(null);
  const [calendarBodyHeight, setCalendarBodyHeight] = useState(0);

  useEffect(() => {
    const el = calendarFitRef.current;
    if (!el) return;
    const measure = () => setCalendarBodyHeight(el.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [view, layoutView, isLoading, perspective]);

  const hourCount = Math.max(1, visibleHours.endHour - visibleHours.startHour);
  const headerPx =
    CALENDAR_COLUMN_HEADER_PX +
    (gridColumns.length > 0 ? CALENDAR_STAFF_HEADER_PX + (resourceColumnsActive ? 12 : 0) : 0);
  const hourHeight = fitHourHeight(Math.max(0, calendarBodyHeight - headerPx), hourCount);

  // Realtime live-update subscriptions
  useAppointmentsRealtime(activeClinicId, range);
  useGroupSessionsRealtime(activeClinicId, range);

  const reschedule = useRescheduleAppointment();
  const updateDuration = useUpdateAppointmentDuration();
  const rescheduleInFlightRef = useRef(false);

  const [modalState, setModalState] = useState<
    | {
        type: 'appointment';
        mode: 'create';
        initialStart: Date | null;
        initialEnd: Date | null;
        initialClinicianId?: string | null;
        initialResourceIds?: string[] | null;
        initialIntent?: 'appointment' | 'block';
      }
    | { type: 'appointment'; mode: 'edit'; appointmentId: string }
    | { type: 'appointment'; mode: 'details'; appointmentId: string }
    | { type: 'group_session'; mode: 'create'; initialStart: Date | null }
    | { type: 'group_session'; mode: 'edit'; sessionId: string }
    | null
  >(null);
  const isDesktopNav = useIsDesktopNav();
  const weekDays = useMemo(() => clinicWeekDays(anchor), [anchor]);

  const monthLabel = useMemo(
    () =>
      formatAppointmentRangeLabel({
        view,
        anchor,
        language: i18n.language,
        weekTitle: (week) => t('appointments.week_n', { week }),
        timeZone: CLINIC_TIMEZONE,
      }),
    [view, anchor, i18n.language, t],
  );
  const periodWidthSamples = useMemo(() => {
    const weekTitle = (week: number) => t('appointments.week_n', { week });
    const samples = appointmentPeriodLabelWidthSamples(i18n.language, weekTitle);
    if (!samples.includes(monthLabel)) samples.push(monthLabel);
    return samples;
  }, [i18n.language, t, monthLabel]);

  // DnD sensors: require 8px movement before activating (preserves click handlers)
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }));

  // Stable reference to the current range query key for optimistic cache updates
  const rangeQueryKey = useMemo(
    () => appointmentsRangeQueryKey(activeClinicId ?? null, range),
    [activeClinicId, range],
  );

  function handleDragStart(event: DragStartEvent) {
    const data = event.active.data.current as DragData | undefined;
    if (data) setActiveDrag(data);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over, delta } = event;
    setActiveDrag(null);

    if (!over || !active.data.current) return;

    const dragData = active.data.current as DragData;
    const { kind, appointment } = dragData;
    const overData = over.data.current as DropData | undefined;
    if (!overData?.date) return;

    const droppedAt = new Date(overData.date);
    const targetKey = clinicDayKey(droppedAt);
    const [targetYear, targetMonth, targetDayNumber] = targetKey.split('-').map(Number);
    const targetDay = parseClinicDayKey(targetKey) ?? clinicStartOfDay(droppedAt);
    const origStart = new Date(appointment.starts_at);
    const origEnd = new Date(appointment.ends_at);
    const durationMs = origEnd.getTime() - origStart.getTime();
    const origStartMinutes = Math.round(clinicMinutesFromMidnight(origStart));
    const deltaMinutes = snapToFifteenMin(delta.y, hourHeight);

    if (kind === 'reschedule') {
      const rawMinutes = origStartMinutes + deltaMinutes;
      // Clamp so appointment stays within the visible window
      const clampedMinutes = Math.max(
        visibleHours.startHour * 60,
        Math.min((visibleHours.endHour - 1) * 60 - 14, rawMinutes),
      );
      const newStartsAt = zonedInstant(
        targetYear,
        targetMonth,
        targetDayNumber,
        Math.floor(clampedMinutes / 60),
        clampedMinutes % 60,
      );
      const newEndsAt = new Date(newStartsAt.getTime() + durationMs);

      const timeChanged =
        newStartsAt.toISOString() !== origStart.toISOString() ||
        !sameClinicDay(targetDay, origStart);

      const resolved = resolveClinicHoursForLocalDate({
        date: newStartsAt,
        weeklyHours,
        exception: exceptionsMap.get(clinicDayKey(newStartsAt)) ?? null,
      });
      const targetClinicianId = resourceViewActive
        ? appointment.clinician_id
        : (overData.resourceId ?? appointment.clinician_id);
      const targetIso = clinicDayKey(newStartsAt);
      const clinicWeekday = clinicWeekdayIndex(newStartsAt);
      const clinicJsDay = clinicWeekday === 6 ? 0 : clinicWeekday + 1;
      const targetMember = (members ?? []).find((item) => item.user_id === targetClinicianId);
      const practitionerPeriods = getEffectiveBookableWindows({
        clinicPeriods: resolved.periods,
        clinicSource: resolved.source,
        clinicExceptionType: resolved.exceptionType,
        clinicianDay: targetMember?.working_hours[WORKING_HOURS_DAY_KEYS[clinicJsDay]],
        memberExceptions: toMemberDayExceptions(
          memberExceptions,
          targetMember?.membership_id,
          targetIso,
        ),
      });
      const outsideOpeningHours = outsideShownCalendarHours(
        resolved,
        practitionerPeriods,
        newStartsAt,
        newEndsAt,
      );
      const closedException =
        isClosedHoursException(resolved) && clinicDayKey(newStartsAt) !== clinicDayKey(origStart);

      if (resourceViewActive) {
        if (!timeChanged) {
          toast.error(t('appointments.resource_drag_reassign_unsupported'));
          return;
        }
        setPendingReschedule({
          appointment,
          newStartsAt,
          newEndsAt,
          newClinicianId: appointment.clinician_id,
          oldStartsAt: origStart,
          oldEndsAt: origEnd,
          outsideOpeningHours,
          closedException,
          blockOverlapLabel: isCalendarBlock(appointment)
            ? ''
            : dragBlockOverlapLabel({
                appointments: appointments ?? [],
                clinicianId: appointment.clinician_id,
                start: newStartsAt,
                end: newEndsAt,
                excludeId: appointment.id,
                formatClock: (date) => formatAppointmentClock(date, i18n.language),
              }),
        });
        return;
      }

      // Check if clinician changed (staff-column view)
      const newClinicianId = targetClinicianId;

      // Skip if nothing changed
      if (!timeChanged && newClinicianId === appointment.clinician_id) return;

      // Store for confirmation instead of executing immediately
      setPendingReschedule({
        appointment,
        newStartsAt,
        newEndsAt,
        newClinicianId,
        oldStartsAt: origStart,
        oldEndsAt: origEnd,
        outsideOpeningHours,
        closedException,
        blockOverlapLabel: isCalendarBlock(appointment)
          ? ''
          : dragBlockOverlapLabel({
              appointments: appointments ?? [],
              clinicianId: newClinicianId,
              start: newStartsAt,
              end: newEndsAt,
              excludeId: appointment.id,
              formatClock: (date) => formatAppointmentClock(date, i18n.language),
            }),
      });
    }

    if (kind === 'resize-end') {
      const origEndMinutes = Math.round(clinicMinutesFromMidnight(origEnd));
      const rawEndMinutes = origEndMinutes + deltaMinutes;
      // Minimum 15 min duration; cap at end of visible day
      const clampedEndMinutes = Math.max(
        origStartMinutes + 15,
        Math.min(visibleHours.endHour * 60, rawEndMinutes),
      );
      // Resize stays on the same day as the original appointment
      const originKey = clinicDayKey(origStart);
      const [originYear, originMonth, originDay] = originKey.split('-').map(Number);
      const newEndsAt = zonedInstant(
        originYear,
        originMonth,
        originDay,
        Math.floor(clampedEndMinutes / 60),
        clampedEndMinutes % 60,
      );

      if (newEndsAt.toISOString() === origEnd.toISOString()) return;

      if (!isCalendarBlock(appointment)) {
        const blockLabel = dragBlockOverlapLabel({
          appointments: appointments ?? [],
          clinicianId: appointment.clinician_id,
          start: origStart,
          end: newEndsAt,
          excludeId: appointment.id,
          formatClock: (date) => formatAppointmentClock(date, i18n.language),
        });
        if (blockLabel) {
          const proceed = window.confirm(
            t('appointments.modal.block_overlap_confirm', { label: blockLabel }),
          );
          if (!proceed) return;
        }
      }

      const prevData = qc.getQueryData<AppointmentListItem[]>(rangeQueryKey);
      qc.setQueryData<AppointmentListItem[]>(rangeQueryKey, (old) =>
        (old ?? []).map((a) =>
          a.id === appointment.id ? { ...a, ends_at: newEndsAt.toISOString() } : a,
        ),
      );

      updateDuration.mutate(
        {
          appointment_id: appointment.id,
          new_ends_at: newEndsAt.toISOString(),
        },
        {
          onError: (err) => {
            qc.setQueryData(rangeQueryKey, prevData);
            const key = appointmentSaveErrorI18nKey(err, true);
            if (
              key === 'appointments.errors.reschedule_unavailable' ||
              key === 'appointments.errors.resource_selected_unavailable' ||
              key === 'appointments.errors.resource_required_unavailable'
            ) {
              setConflictAptId(appointment.id);
              setTimeout(() => setConflictAptId(null), 2000);
            }
            toast.error(t(key));
          },
          onSuccess: () => toast.success(t('appointments.duration_updated')),
        },
      );
    }
  }

  function handleDragCancel() {
    setActiveDrag(null);
  }

  async function executeReschedule() {
    if (!pendingReschedule || rescheduleInFlightRef.current || reschedule.isPending) return;

    const { appointment, newStartsAt, newEndsAt, newClinicianId } = pendingReschedule;
    rescheduleInFlightRef.current = true;

    const prevData = qc.getQueryData<AppointmentListItem[]>(rangeQueryKey);
    qc.setQueryData<AppointmentListItem[]>(rangeQueryKey, (old) =>
      (old ?? []).map((a) =>
        a.id === appointment.id
          ? {
              ...a,
              starts_at: newStartsAt.toISOString(),
              ends_at: newEndsAt.toISOString(),
              clinician_id: newClinicianId,
            }
          : a,
      ),
    );

    try {
      await reschedule.mutateAsync({
        appointment_id: appointment.id,
        starts_at: newStartsAt.toISOString(),
        ends_at: newEndsAt.toISOString(),
        clinician_id: newClinicianId,
      });
      if (appointment.patient_id) {
        sendStaffAppointmentEmail(appointment.id, 'rescheduled');
      }
      setPendingReschedule(null);
      toast.success(t('appointments.reschedule_success'));
    } catch (err) {
      qc.setQueryData(rangeQueryKey, prevData);
      const kind = classifyAppointmentRescheduleError(err);
      if (kind === 'conflict' || kind === 'resource_selected' || kind === 'resource_required') {
        setConflictAptId(appointment.id);
        setTimeout(() => setConflictAptId(null), 2000);
      }
      console.error('reschedule_appointment failed', err);
      toast.error(t(appointmentRescheduleErrorI18nKey(kind)));
    } finally {
      rescheduleInFlightRef.current = false;
    }
  }

  function openCreateAppointment() {
    setModalState({
      type: 'appointment',
      mode: 'create',
      initialStart: null,
      initialEnd: null,
    });
  }

  const bookCourseOpen = searchParams.get('bookCourse') === '1';
  const canBookCourse = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'appointments', 'create'),
  );

  function openBookCourse() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('bookCourse', '1');
        return next;
      },
      { replace: true },
    );
  }

  function closeBookCourse() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('bookCourse');
        return next;
      },
      { replace: true },
    );
  }

  const navPrevLabel =
    view === 'day'
      ? t('appointments.prev_day')
      : view === 'month' || view === 'agenda'
        ? t('appointments.prev_month')
        : t('appointments.prev_week');
  const navNextLabel =
    view === 'day'
      ? t('appointments.next_day')
      : view === 'month' || view === 'agenda'
        ? t('appointments.next_month')
        : t('appointments.next_week');

  const practitionerFilterAllLabel = t('appointments.filter_practitioner_all');
  const allEmployeesLabel = t('appointments.filter_all_employees');
  const staffFilterMembers = resourceViewActive
    ? (members ?? [])
    : showAllEmployees || !rolesReady
      ? (members ?? [])
      : withStickySelectedMembers(practitionerMembers, members ?? [], [clinicianFilter]);
  const longestPractitionerFilterLabel = staffFilterMembers.reduce(
    (longest, member) => (member.name.length > longest.length ? member.name : longest),
    resourceViewActive
      ? practitionerFilterAllLabel
      : allEmployeesLabel.length > practitionerFilterAllLabel.length
        ? allEmployeesLabel
        : practitionerFilterAllLabel,
  );
  const staffFilterValue = resourceViewActive
    ? (clinicianFilter ?? '')
    : showAllEmployees
      ? APPOINTMENTS_STAFF_ALL_EMPLOYEES
      : (clinicianFilter ?? '');

  const showMobileAgenda = !isDesktopNav && (view === 'day' || view === 'week');
  const listedAppointments = countedPatientAppointments(calendarAppointments);
  const mobileDayAppointments = listedAppointments.filter((apt) =>
    sameClinicDay(new Date(apt.starts_at), anchor),
  );
  const weekStripCounts = weekDays.map(
    (day) => listedAppointments.filter((apt) => sameClinicDay(new Date(apt.starts_at), day)).length,
  );

  function openCalendarItem(id: string) {
    const item = (appointments ?? []).find((appointment) => appointment.id === id);
    if (item && isCalendarBlock(item)) {
      setModalState({ type: 'appointment', mode: 'edit', appointmentId: id });
      return;
    }
    setModalState({ type: 'appointment', mode: 'details', appointmentId: id });
  }

  function openSlotMenu(
    start: Date,
    end: Date | null,
    columnId: string | undefined,
    point?: { x: number; y: number },
  ) {
    setSlotMenu({
      start,
      end,
      clinicianId: resourceViewActive ? clinicianFilter : (columnId ?? clinicianFilter),
      resourceIds:
        resourceViewActive && columnId && columnId !== 'unassigned' ? [columnId] : undefined,
      x: point?.x ?? 24,
      y: point?.y ?? 80,
    });
  }

  if (!activeClinicId) {
    return (
      <PageLayout>
        <ErrorAlert message={t('appointments.no_clinic')} />
      </PageLayout>
    );
  }

  return (
    <PageLayout fill>
      <div className="flex w-full min-w-0 flex-wrap items-center gap-s3">
        <div className="mr-auto shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('appointments.title')}</h1>
          <div className="mt-s1 grid">
            {periodWidthSamples.map((sample) => (
              <span
                key={sample}
                aria-hidden
                className={cn(TYPE.body, 'invisible col-start-1 row-start-1 whitespace-nowrap')}
              >
                {sample}
              </span>
            ))}
            <p className={cn(TYPE.body, 'col-start-1 row-start-1 whitespace-nowrap text-ds-muted')}>
              {monthLabel}
            </p>
          </div>
        </div>
        <div className="contents">
          {(layoutView === 'time' || resourceViewActive) && (
            <div className="grid w-max min-w-[16rem] max-w-[24rem] shrink-0">
              <span
                aria-hidden
                className="invisible col-start-1 row-start-1 whitespace-nowrap pl-3 pr-10 text-[15px] leading-6"
              >
                {longestPractitionerFilterLabel}
              </span>
              <Select
                className="col-start-1 row-start-1 min-w-0"
                value={staffFilterValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setClinicianFilter(
                    value === APPOINTMENTS_STAFF_ALL_EMPLOYEES ? value : value || null,
                  );
                }}
                aria-label={t('appointments.practitioner')}
              >
                <option value="">{practitionerFilterAllLabel}</option>
                {!resourceViewActive ? (
                  <option value={APPOINTMENTS_STAFF_ALL_EMPLOYEES}>{allEmployeesLabel}</option>
                ) : null}
                {staffFilterMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {t('appointments.filter_practitioner', { name: m.name })}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="flex shrink-0 items-center gap-s2">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={() => {
                if (view === 'day') {
                  setAnchor((a) => clinicAddDays(a, -1));
                } else if (view === 'month' || view === 'agenda') {
                  setAnchor((a) => clinicAddMonths(a, -1));
                } else {
                  setAnchor((a) => clinicAddDays(a, -7));
                }
              }}
              aria-label={navPrevLabel}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAnchor(clinicStartOfDay(new Date()))}
            >
              {t('appointments.today')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={() => {
                if (view === 'day') {
                  setAnchor((a) => clinicAddDays(a, 1));
                } else if (view === 'month' || view === 'agenda') {
                  setAnchor((a) => clinicAddMonths(a, 1));
                } else {
                  setAnchor((a) => clinicAddDays(a, 7));
                }
              }}
              aria-label={navNextLabel}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {(layoutView === 'time' || resourceViewActive) && (
            <SegmentedControl
              className="shrink-0"
              ariaLabel={t('appointments.view_label')}
              value={view}
              onChange={(next) => {
                setAnchor((current) => anchorForCalendarView(current));
                setView(next);
              }}
              options={[
                { value: 'day', label: t('appointments.view_day') },
                { value: 'week', label: t('appointments.view_week') },
                { value: 'month', label: t('appointments.view_month') },
                { value: 'agenda', label: t('appointments.view_agenda') },
              ]}
            />
          )}

          {resourceViewActive && (
            <div className="shrink-0">
              <CalendarResourceFilter
                groups={resourceGroups ?? []}
                resources={clinicResources ?? []}
                selectedIds={selectedResourceIds}
                onChange={setSelectedResourceIds}
              />
            </div>
          )}

          {!resourceViewActive && layoutView === 'resource' && (
            <select
              multiple
              value={selectedStaffIds ?? []}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
                setSelectedStaffIds(selected.length === 0 ? null : selected);
              }}
              className={cn(TOOLBAR_CONTROL, 'h-auto min-w-[10.5rem] py-1 text-ds-text')}
              size={3}
            >
              {members?.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}

          {!resourceViewActive && (
            <ToolbarCluster className="shrink-0" data-testid="calendar-layout-toggle">
              <ToolbarSegment
                active={layoutView === 'time'}
                className="px-2.5"
                onClick={() => setLayoutView('time')}
                aria-label={t('appointments.time_view')}
              >
                <Calendar className="h-4 w-4" />
              </ToolbarSegment>
              <ToolbarSegment
                active={layoutView === 'resource'}
                className="px-2.5"
                onClick={() => setLayoutView('resource')}
                aria-label={t('appointments.resource_view')}
              >
                <Users className="h-4 w-4" />
              </ToolbarSegment>
            </ToolbarCluster>
          )}

          <ToolbarCluster className="shrink-0" data-testid="calendar-perspective-toggle">
            <ToolbarSegment
              active={perspective === 'practitioners'}
              onClick={() => setPerspective('practitioners')}
            >
              {t('appointments.perspective.practitioners')}
            </ToolbarSegment>
            <ToolbarSegment
              active={perspective === 'resources'}
              onClick={() => setPerspective('resources')}
            >
              {t('appointments.perspective.resources')}
            </ToolbarSegment>
          </ToolbarCluster>

          <Button className="shrink-0" onClick={openCreateAppointment}>
            <Plus className="h-4 w-4" />
            {t('appointments.new_appointment')}
          </Button>
          {canBookCourse ? (
            <Button className="shrink-0" type="button" variant="secondary" onClick={openBookCourse}>
              {t('treatment_courses.book_title')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex h-0 min-h-0 flex-1 flex-col overflow-x-hidden">
        {error && (
          <div className="pt-s3">
            <Banner
              tone="error"
              title={t('appointments.load_error')}
              action={
                <Button type="button" variant="secondary" onClick={() => void refetch()}>
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        )}
        {isLoading ? (
          <div className="space-y-s3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-[480px] w-full" />
          </div>
        ) : showMobileAgenda ? (
          <div className="min-h-0 flex-1 overflow-auto pt-s3">
            <WeekStrip
              days={weekDays}
              selected={anchor}
              counts={weekStripCounts}
              onSelect={setAnchor}
            />
            <div className="mt-s4">
              <AgendaView
                appointments={mobileDayAppointments}
                metaById={calendarMeta}
                onAppointmentClick={openCalendarItem}
              />
            </div>
            <CalendarLegend />
          </div>
        ) : view === 'month' ? (
          <div className="min-h-0 flex-1 overflow-auto">
            <MonthView
              anchor={anchor}
              appointments={calendarAppointments}
              onDayClick={(date) => {
                setAnchor(clinicStartOfDay(date));
                setView('day');
              }}
              onAppointmentClick={openCalendarItem}
              hourExceptions={exceptionsMap}
              resolvedHoursByDate={resolvedHoursByDate}
              onDayHoursClick={(date) => setHoursDate(date)}
            />
            <CalendarLegend />
          </div>
        ) : view === 'agenda' ? (
          <div className="min-h-0 flex-1 overflow-auto">
            <AgendaView
              appointments={listedAppointments}
              metaById={calendarMeta}
              onAppointmentClick={openCalendarItem}
            />
            <CalendarLegend />
          </div>
        ) : resourceColumnsActive && !hasActiveResources ? (
          <Card className="m-s4" data-testid="calendar-resources-empty">
            <EmptyState
              icon={<Users className="h-7 w-7" />}
              title={t('appointments.resource_empty.title')}
              description={t('appointments.resource_empty.body')}
              action={
                <Button onClick={() => navigate('/settings/resources')}>
                  {t('appointments.resource_empty.action')}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="flex h-0 min-h-0 min-w-0 flex-1 flex-col">
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div
                ref={calendarFitRef}
                className="flex h-0 min-h-0 min-w-0 flex-1 flex-col overflow-hidden pt-s3"
              >
                <CalendarGrid
                  days={days}
                  appointments={calendarAppointments}
                  groupSessions={calendarGroupSessions}
                  conflictAptId={conflictAptId}
                  isDnDActive={activeDrag !== null}
                  staffColumns={gridColumns}
                  columnPerspective={resourceColumnsActive ? 'resource' : 'practitioner'}
                  resourceIdsByAppointment={resourceIdsByAppointment}
                  showClinicianOnCards={resourceColumnsActive}
                  unassignedLabel={
                    resourceColumnsActive ? t('appointments.resource_unallocated') : undefined
                  }
                  metaById={calendarMeta}
                  bookingHours={bookingHours}
                  hourHeight={hourHeight}
                  dayStartHour={visibleHours.startHour}
                  dayEndHour={visibleHours.endHour}
                  openingHoursMidpoint={openingHoursMidpoint}
                  openingHoursReady={openingHoursReady}
                  openingHoursContextKey={[
                    layoutView,
                    perspective,
                    clinicianFilter ?? 'all',
                    (selectedStaffIds ?? []).slice().sort().join(','),
                    (selectedResourceIds ?? []).slice().sort().join(','),
                    days.map((day) => clinicDayKey(day)).join(','),
                  ].join('|')}
                  resolvedHoursByDate={resolvedHoursByDate}
                  resolvedHoursByPractitionerDate={resolvedHoursByPractitionerDate}
                  columnHoursByDate={resourceColumnsActive ? undefined : practitionerColumnHours}
                  hoursColumnId={
                    !resourceColumnsActive && staffColumns.length === 0 ? clinicianFilter : null
                  }
                  onColumnDayHoursClick={
                    resourceColumnsActive
                      ? undefined
                      : (date, userId) => setPractitionerHoursTarget({ date, userId })
                  }
                  onSelectDay={(date) => setAnchor(clinicStartOfDay(date))}
                  onDayHoursClick={(date) => {
                    setAnchor(clinicStartOfDay(date));
                    setHoursDate(date);
                  }}
                  onSlotClick={(start, columnId, point) => {
                    setAnchor(clinicStartOfDay(start));
                    openSlotMenu(start, null, columnId, point);
                  }}
                  onSlotDragCreate={(start, end, columnId, point) => {
                    setAnchor(clinicStartOfDay(start));
                    openSlotMenu(start, end, columnId, point);
                  }}
                  onAppointmentClick={openCalendarItem}
                  onGroupSessionClick={(id) =>
                    setModalState({ type: 'group_session', mode: 'edit', sessionId: id })
                  }
                />
                <CalendarLegend className="shrink-0 pb-s4" />
              </div>

              {/* Ghost overlay follows cursor while dragging */}
              <DragOverlay dropAnimation={null}>
                {activeDrag ? (
                  <AppointmentGhost
                    appointment={activeDrag.appointment}
                    language={i18n.language}
                    hourHeight={hourHeight}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </div>

      {bookCourseOpen && activeClinicId ? (
        <BookTreatmentCourseSlideOver
          clinicId={activeClinicId}
          onClose={closeBookCourse}
          onCreated={(created) => {
            const params = writeTreatmentCoursePatientParams(new URLSearchParams(), {
              courseId: created.id,
              stepId: null,
              edit: false,
              journal: false,
            });
            navigate(`/patients/${created.patientId}?${params.toString()}`);
          }}
        />
      ) : null}

      {modalState && activeClinicId && (
        <>
          {modalState.type === 'appointment' && modalState.mode === 'details' && (
            <AppointmentDetailsModal
              appointmentId={modalState.appointmentId}
              clinicId={activeClinicId}
              onClose={() => setModalState(null)}
              onEdit={() => {
                const appointmentId = modalState.appointmentId;
                setModalState({ type: 'appointment', mode: 'edit', appointmentId });
              }}
            />
          )}
          {modalState.type === 'appointment' &&
            (modalState.mode === 'create' || modalState.mode === 'edit') && (
              <AppointmentModal
                clinicId={activeClinicId}
                mode={modalState.mode}
                appointmentId={modalState.mode === 'edit' ? modalState.appointmentId : null}
                initialStart={modalState.mode === 'create' ? modalState.initialStart : null}
                initialEnd={modalState.mode === 'create' ? modalState.initialEnd : null}
                initialIntent={modalState.mode === 'create' ? modalState.initialIntent : undefined}
                initialClinicianId={
                  modalState.mode === 'create'
                    ? (modalState.initialClinicianId ?? clinicianFilter)
                    : clinicianFilter
                }
                initialResourceIds={
                  modalState.mode === 'create' ? modalState.initialResourceIds : undefined
                }
                onClose={() => setModalState(null)}
              />
            )}
          {modalState.type === 'group_session' && (
            <GroupSessionModal
              clinicId={activeClinicId}
              mode={modalState.mode}
              sessionId={modalState.mode === 'edit' ? modalState.sessionId : null}
              initialStart={modalState.mode === 'create' ? modalState.initialStart : null}
              onClose={() => setModalState(null)}
            />
          )}
        </>
      )}

      {/* Reschedule Confirmation Modal */}
      {slotMenu ? (
        <SlotActionMenu
          x={slotMenu.x}
          y={slotMenu.y}
          onClose={() => setSlotMenu(null)}
          onAppointment={() => {
            const menu = slotMenu;
            setSlotMenu(null);
            setModalState({
              type: 'appointment',
              mode: 'create',
              initialStart: menu.start,
              initialEnd: menu.end,
              initialClinicianId: menu.clinicianId,
              initialResourceIds: menu.resourceIds,
              initialIntent: 'appointment',
            });
          }}
          onBlock={() => {
            const menu = slotMenu;
            setSlotMenu(null);
            setModalState({
              type: 'appointment',
              mode: 'create',
              initialStart: menu.start,
              initialEnd: menu.end,
              initialClinicianId: menu.clinicianId,
              initialResourceIds: menu.resourceIds,
              initialIntent: 'block',
            });
          }}
        />
      ) : null}

      {pendingReschedule && (
        <RescheduleConfirmationModal
          patientName={pendingReschedule.appointment.patient_name}
          serviceName={pendingReschedule.appointment.service_name ?? undefined}
          oldDate={pendingReschedule.oldStartsAt}
          newDate={pendingReschedule.newStartsAt}
          oldTime={`${formatAppointmentClock(pendingReschedule.oldStartsAt, i18n.language)}–${formatAppointmentClock(pendingReschedule.oldEndsAt, i18n.language)}`}
          newTime={`${formatAppointmentClock(pendingReschedule.newStartsAt, i18n.language)}–${formatAppointmentClock(pendingReschedule.newEndsAt, i18n.language)}`}
          onConfirm={() => {
            void executeReschedule();
          }}
          onCancel={() => {
            if (!reschedule.isPending && !rescheduleInFlightRef.current) {
              setPendingReschedule(null);
            }
          }}
          isConfirming={reschedule.isPending}
          outsideOpeningHours={pendingReschedule.outsideOpeningHours}
          closedException={pendingReschedule.closedException}
          blockOverlapLabel={
            pendingReschedule.blockOverlapLabel
              ? t('appointments.modal.block_overlap_confirm', {
                  label: pendingReschedule.blockOverlapLabel,
                })
              : null
          }
        />
      )}

      {hoursDate && activeClinicId && (
        <ClinicDayHoursModal
          clinicId={activeClinicId}
          date={hoursDate}
          isoDate={clinicDayKey(hoursDate)}
          weeklyHours={weeklyHours}
          exception={exceptionsMap.get(clinicDayKey(hoursDate)) ?? null}
          practitionerIds={openingHoursMembers.map((member) => member.user_id)}
          practitionerNames={openingHoursMembers.map((member) => member.name)}
          practitionerWeekly={
            openingHoursMembers.length === 1
              ? (openingHoursMembers[0]?.working_hours[
                  WORKING_HOURS_DAY_KEYS[hoursDate.getDay()]
                ] ?? null)
              : null
          }
          memberExceptions={
            openingHoursMembers.length === 1
              ? toMemberDayExceptions(
                  memberExceptions,
                  openingHoursMembers[0]?.membership_id,
                  clinicDayKey(hoursDate),
                )
              : []
          }
          canEdit={canEditHours}
          onClose={() => setHoursDate(null)}
        />
      )}
      {practitionerHoursTarget &&
        activeClinicId &&
        (() => {
          const member = (members ?? []).find(
            (row) => row.user_id === practitionerHoursTarget.userId,
          );
          if (!member) return null;
          return (
            <PractitionerDayHoursModal
              clinicId={activeClinicId}
              date={practitionerHoursTarget.date}
              isoDate={clinicDayKey(practitionerHoursTarget.date)}
              practitionerName={member.name}
              membershipId={member.membership_id}
              workingHours={member.working_hours}
              exceptions={practitionerExceptions}
              canEdit={Boolean(canEditHours || (user?.id && user.id === member.user_id))}
              onClose={() => setPractitionerHoursTarget(null)}
            />
          );
        })()}
    </PageLayout>
  );
}
